import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Edit, Play, Pause, CheckCircle, Clock, Package, Users, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import ManufacturingService from '@/api/manufacturing.service';
import { canEdit } from '@/lib/permissions';

const ProductionJobDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [productionJob, setProductionJob] = useState<any>(null);

  const businessId = parseInt(localStorage.getItem('businessId') || '1');

  useEffect(() => {
    if (id) {
      fetchProductionJob();
    }
  }, [id]);

  const fetchProductionJob = async () => {
    try {
      setIsLoading(true);
      const response = await ManufacturingService.getProductionJob(businessId, parseInt(id!));
      
      if (response.data?.success) {
        setProductionJob(response.data.data);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load production job details",
        });
        navigate('/manufacturing/production-jobs');
      }
    } catch (error: any) {
      console.error('Error fetching production job:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load production job details",
      });
      navigate('/manufacturing/production-jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const response = await ManufacturingService.updateProductionJobStatus(businessId, parseInt(id!), newStatus);
      
      if (response.data?.success) {
        toast({
          title: "Success",
          description: `Production job ${newStatus} successfully`,
        });
        fetchProductionJob(); // Refresh data
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to update production job status",
        });
      }
    } catch (error: any) {
      console.error('Error updating status:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update production job status",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      planned: { variant: 'secondary' as const, label: 'Planned' },
      in_progress: { variant: 'default' as const, label: 'In Progress' },
      completed: { variant: 'default' as const, label: 'Completed' },
      cancelled: { variant: 'destructive' as const, label: 'Cancelled' }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { variant: 'secondary' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { variant: 'secondary' as const, label: 'Low' },
      medium: { variant: 'default' as const, label: 'Medium' },
      high: { variant: 'destructive' as const, label: 'High' },
      urgent: { variant: 'destructive' as const, label: 'Urgent' }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || { variant: 'secondary' as const, label: priority };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading production job details...</span>
        </div>
      </div>
    );
  }

  if (!productionJob) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <h3 className="text-lg font-semibold">Production Job Not Found</h3>
          <p className="text-muted-foreground">The requested production job could not be found.</p>
          <Button 
            onClick={() => navigate('/manufacturing/production-jobs')}
            className="mt-4"
          >
            Back to Production Jobs
          </Button>
        </div>
      </div>
    );
  }

  const progressPercentage = productionJob.actualQuantity ? 
    Math.min((productionJob.actualQuantity / productionJob.plannedQuantity) * 100, 100) : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/manufacturing/production-jobs')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Production Jobs
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {productionJob.status === 'planned' && (
            <Button
              variant="outline"
              onClick={() => handleStatusChange('in_progress')}
              className="flex items-center gap-2"
            >
              <Play className="h-4 w-4" />
              Start Job
            </Button>
          )}
          {productionJob.status === 'in_progress' && (
            <>
              <Button
                variant="outline"
                onClick={() => handleStatusChange('paused')}
                className="flex items-center gap-2"
              >
                <Pause className="h-4 w-4" />
                Pause Job
              </Button>
              <Button
                variant="outline"
                onClick={() => handleStatusChange('completed')}
                className="flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                Complete Job
              </Button>
            </>
          )}
          {canEdit('Manufacturing') && productionJob.status !== 'completed' && (
            <Button
              onClick={() => navigate(`/manufacturing/production-jobs/${productionJob.id}/edit`)}
              className="flex items-center gap-2"
            >
              <Edit className="h-4 w-4" />
              Edit Job
            </Button>
          )}
        </div>
      </div>

      <div>
        <div className="flex items-center gap-4 mb-2">
          <h1 className="text-3xl font-bold tracking-tight">{productionJob.jobNumber}</h1>
          {getStatusBadge(productionJob.status)}
          {getPriorityBadge(productionJob.priority)}
        </div>
        <p className="text-muted-foreground">
          Recipe: {productionJob.recipeName} • Planned: {productionJob.plannedQuantity} units
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Progress</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{progressPercentage.toFixed(1)}%</div>
            <Progress value={progressPercentage} className="mt-2" />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Planned Qty</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{productionJob.plannedQuantity}</div>
            <p className="text-xs text-muted-foreground">units</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actual Qty</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{productionJob.actualQuantity || 0}</div>
            <p className="text-xs text-muted-foreground">produced</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Duration</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{productionJob.actualDuration || 0}</div>
            <p className="text-xs text-muted-foreground">hours</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="materials">Materials</TabsTrigger>
          <TabsTrigger value="progress">Progress</TabsTrigger>
          <TabsTrigger value="quality">Quality</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Job Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Job Number</label>
                  <p className="text-sm">{productionJob.jobNumber}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Recipe</label>
                  <p className="text-sm">{productionJob.recipeName}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Priority</label>
                  <div className="mt-1">{getPriorityBadge(productionJob.priority)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">{getStatusBadge(productionJob.status)}</div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Schedule</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Scheduled Start</label>
                  <p className="text-sm">
                    {productionJob.scheduledStartDate ? 
                      new Date(productionJob.scheduledStartDate).toLocaleString() : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Scheduled End</label>
                  <p className="text-sm">
                    {productionJob.scheduledEndDate ? 
                      new Date(productionJob.scheduledEndDate).toLocaleString() : 'Not scheduled'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Assigned To</label>
                  <p className="text-sm">{productionJob.assignedToName || 'Unassigned'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created</label>
                  <p className="text-sm">{new Date(productionJob.createdAt).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {productionJob.notes && (
            <Card>
              <CardHeader>
                <CardTitle>Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{productionJob.notes}</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="materials" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Material Consumption</CardTitle>
              <CardDescription>Materials required and consumed for this production job</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Material</TableHead>
                    <TableHead>Required</TableHead>
                    <TableHead>Consumed</TableHead>
                    <TableHead>Remaining</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {productionJob.materials?.map((material: any, index: number) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{material.itemName}</TableCell>
                      <TableCell>{material.requiredQuantity} {material.unit}</TableCell>
                      <TableCell>{material.consumedQuantity || 0} {material.unit}</TableCell>
                      <TableCell>
                        {(material.requiredQuantity - (material.consumedQuantity || 0))} {material.unit}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          (material.consumedQuantity || 0) >= material.requiredQuantity ? 'default' : 'secondary'
                        }>
                          {(material.consumedQuantity || 0) >= material.requiredQuantity ? 'Complete' : 'Pending'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="progress" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Production Progress</CardTitle>
              <CardDescription>Track production milestones and outputs</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Overall Progress</span>
                  <span className="text-sm text-muted-foreground">{progressPercentage.toFixed(1)}%</span>
                </div>
                <Progress value={progressPercentage} />
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Planned Quantity</label>
                    <p className="text-2xl font-bold">{productionJob.plannedQuantity}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Actual Quantity</label>
                    <p className="text-2xl font-bold">{productionJob.actualQuantity || 0}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quality" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Quality Checks</CardTitle>
              <CardDescription>Quality control results for this production job</CardDescription>
            </CardHeader>
            <CardContent>
              {productionJob.qualityChecks && productionJob.qualityChecks.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Check Point</TableHead>
                      <TableHead>Specification</TableHead>
                      <TableHead>Actual</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Date</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {productionJob.qualityChecks.map((check: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{check.checkPoint}</TableCell>
                        <TableCell>{check.specification}</TableCell>
                        <TableCell>{check.actualValue}</TableCell>
                        <TableCell>
                          <Badge variant={check.result === 'pass' ? 'default' : 'destructive'}>
                            {check.result}
                          </Badge>
                        </TableCell>
                        <TableCell>{new Date(check.date).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground">No quality checks recorded yet.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductionJobDetail; 